import { useEffect, useState } from "react";
import { CategorySummary } from "@/components/ui/category-summary";
import { useCategorySummaryStore } from "@/components/ui/category-summary/hooks/useCategorySummaryStore";
import { DisplayGroup } from "@/models/displayGroup";

// 比較モード用の定数
const DEFAULT_MIN_OVERVIEW_HEIGHT = 150; // 概要の最小高さ（px）

interface Props {
  displayGroup: DisplayGroup;
  isComparisionMode: boolean;
}

const CategorySummaryContainer: React.FC<Props> = ({ displayGroup, isComparisionMode }) => {
  // 比較モード用の状態変数
  const [leftPanelOverviewHeight, setLeftPanelOverviewHeight] = useState<number>(0);
  const [rightPanelOverviewHeight, setRightPanelOverviewHeight] = useState<number>(0);

  const {
    mainSelectedAnalysisId,
    mainSelectedCategoryPath,
    setCompSelectedAnalysisId,
    setCompSelectedCategoryPath,
  } = useCategorySummaryStore();

  // 最大の概要高さと最大の問題数を計算
  const [overviewHeight, setOverviewHeight] = useState<number>(0);

  // 単一分析→比較モード切り替え時に右側の選択状態（分析名・カテゴリ）を左側に自動コピー
  useEffect(() => {
    if (isComparisionMode) {
      // 右側（メイン）の選択状態を左側（比較）に常にコピー（リロード時初期化）
      if (!mainSelectedAnalysisId && !mainSelectedCategoryPath) return;
      setCompSelectedAnalysisId(mainSelectedAnalysisId);
      setCompSelectedCategoryPath(mainSelectedCategoryPath);
    }
  }, [
    isComparisionMode,
    mainSelectedAnalysisId,
    mainSelectedCategoryPath,
    setCompSelectedAnalysisId,
    setCompSelectedCategoryPath,
  ]);

  // 比較モード時に値を更新
  useEffect(() => {
    if (isComparisionMode) {
      // 少なくとも一方のパネルの高さが設定されている場合
      if (leftPanelOverviewHeight > 0 || rightPanelOverviewHeight > 0) {
        // 両方のパネルの高さの最大値を使用（長い方に合わせる）
        const maxHeight = Math.max(leftPanelOverviewHeight, rightPanelOverviewHeight);

        setOverviewHeight(maxHeight > 0 ? maxHeight : DEFAULT_MIN_OVERVIEW_HEIGHT); // 最低高さを確保
      } else {
        setOverviewHeight(DEFAULT_MIN_OVERVIEW_HEIGHT); // どちらも0の場合は最低高さを設定
      }
    }
  }, [
    isComparisionMode,
    leftPanelOverviewHeight,
    rightPanelOverviewHeight,
  ]);

  return (
    <>
      {isComparisionMode && (
        <CategorySummary
          className={isComparisionMode && "min-w-[50%] pr-[10px]"}
          displayGroup={displayGroup}
          isMainPanel={false}
          compareMode={{
            overviewHeight: overviewHeight,
            minOverviewHeight: DEFAULT_MIN_OVERVIEW_HEIGHT,
            onOverviewHeightChange: setRightPanelOverviewHeight,
          }}
        />
      )}

      <CategorySummary
        className={isComparisionMode ? "min-w-[50%] pl-[10px]" : ""}
        displayGroup={displayGroup}
        compareMode={isComparisionMode
          ? {
            overviewHeight: overviewHeight,
            minOverviewHeight: DEFAULT_MIN_OVERVIEW_HEIGHT,
            onOverviewHeightChange: setLeftPanelOverviewHeight,
          }
          : undefined}
      />
    </>
  );
};

export default CategorySummaryContainer;
